import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { cn } from 'helpers/classnames';
import type { GenericSizeBiases } from 'types/product';
import { track } from 'apis/amethyst';
import { HYDRA_PRODUCT_SIZING_PREDICTION } from 'constants/hydraTests';
import { sizeBiasImpression } from 'events/product';
import { useInAssignment } from 'hooks/useHydra';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';
import HtmlToReact from 'components/common/HtmlToReact';
import RecommendedSizingLayout from 'components/productdetail/stylepicker/RecommendedSizingLayout/RecommendedSizingLayout';

import css from 'styles/components/productdetail/genericSizeBiasReco.scss';

export interface GenericSizeBiasRecoProps {
  currentProductId: string;
  genericSizeBiases: Partial<GenericSizeBiases>;
  children?: React.ReactNode;
}

export const GenericSizeBiasReco = ({ currentProductId, genericSizeBiases }: GenericSizeBiasRecoProps) => {
  const { testId } = useMartyContext();
  const { productId, sizeBiases, text } = genericSizeBiases;
  const [copyText, setCopyText] = useState(null);

  const isHydraSizePrediction = useInAssignment(HYDRA_PRODUCT_SIZING_PREDICTION);

  useEffect(() => {
    if (copyText && currentProductId === productId) {
      track(() => [sizeBiasImpression, { text: copyText, productId, sizeBiases }]);
    }
  }, [copyText, currentProductId, productId, sizeBiases, text]);

  const copyTextRef = useCallback(
    (node: any) => {
      if (node) {
        setCopyText(node.textContent);
      }
    },
    [setCopyText]
  );

  const copyProps = {
    'className': cn(isHydraSizePrediction ? css.sizingCopy : css.copy),
    'ref': copyTextRef,
    'data-test-id': testId('genericSizeBias')
  };

  const isAccurateSize = useMemo(() => text && text.includes('true to size'), [text]);
  const isGreenBackground = isAccurateSize || !text;

  if (currentProductId !== productId) {
    return null;
  }

  return text ? (
    <RecommendedSizingLayout isHydraSizePrediction={isHydraSizePrediction} backgroundColor={isGreenBackground ? 'green' : 'yellow'}>
      <div className={cn({ [css.recommendedFitText]: isHydraSizePrediction })} data-test-id={testId('genericSizeBiasReco')}>
        <HtmlToReact {...copyProps}>{text}</HtmlToReact>
      </div>
    </RecommendedSizingLayout>
  ) : null;
};

export default withErrorBoundary('GenericSizeBiasReco', GenericSizeBiasReco);
